import _ from 'lodash';
import mime from 'mime';

import AuthApi from '@/store/api/system/auth';

/**
 * What a stored file IS, for surfaces that only list files rather than manage
 * them. The media library answers the same questions inline (twice over, in
 * FilesList and ImageLibraryDialog); this is the extract, so a third list does
 * not invent a fourth vocabulary.
 *
 * Everything here reads a file as it comes back from GraphQL, and copes with
 * the leaner shape a fresh upload has: { id, name, size, url, signed_url }
 * carries no mime type at all, so the filename decides.
 */

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'avif', 'bmp', 'svg'];

/** The extension, uppercase, as the media library labels it. */
export const fileExtension = (file) => {
    const name = String(_.get(file, 'name', '') || '');
    const fromName = name.includes('.') ? name.split('.').pop() : '';
    const extension = fromName || mime.getExtension(_.get(file, 'mime', '') || '') || 'file';

    return String(extension).toUpperCase();
};

/** Whether this file can be shown as a picture rather than described as one. */
export const isImageFile = (file) => {
    if (String(_.get(file, 'mime', '') || '').startsWith('image/')) return true;

    return IMAGE_EXTENSIONS.includes(fileExtension(file).toLowerCase());
};

/**
 * Where the file can actually be fetched. A private file (a ticket's, a
 * message attachment's) is reachable only through its temporary signed URL.
 */
export const fileUrl = (file) => String(_.get(file, 'signed_url') || _.get(file, 'url') || '');

/** The name a person gave the file, falling back to the stored one. */
export const fileName = (file) => _.get(file, 'name') || _.get(file, 'preferred_name') || '';

/**
 * Where a ticket's file is saved FROM: the ticket's own download route, which
 * writes the download into the ticket's history before handing the file out.
 * The token rides in the query because this is a navigation, not a request
 * the client could put a header on. Read at the moment of the click, so a
 * refreshed token is the one that goes.
 */
export const ticketFileDownloadUrl = (ticketId, file) =>
    `${_.trim(process.env.VUE_APP_API_ENDPOINT, '/')}/tickets/${ticketId}/files/${file.id}/download?token=${AuthApi.getToken()}`;

/**
 * Save a file by navigating a throwaway link to it - the media library's own
 * way, and the only one a cross-origin response can trigger: the server's
 * `Content-Disposition: attachment` is what makes the browser save rather
 * than show.
 */
export const saveFromUrl = (url) => {
    const element = document.createElement('a');
    element.setAttribute('href', url);
    element.style.display = 'none';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
};

/**
 * The real pixel size of an image, which PhotoSwipe needs up front or it
 * cannot zoom correctly. The File type carries no dimensions, so they are
 * measured once - the same thing the media library does before opening its
 * lightbox.
 */
export const loadImageDimensions = (source) =>
    new Promise((resolve, reject) => {
        if (!source) {
            reject(new Error('No source'));
            return;
        }

        const image = new Image();
        image.onload = () => resolve({ width: image.naturalWidth, height: image.naturalHeight });
        image.onerror = reject;
        image.src = source;
    });
